import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  Film, Plus, ChevronRight, ChevronDown, Layers, Clapperboard,
  FolderOpen, Trash2, Save, X, Settings2, Clock, Monitor, Database, BookOpen, HardDrive, Cloud,
  AlertTriangle, GitBranch, Tv, ArrowLeft, Info, Building2, Link as LinkIcon, Unlink, Loader2,
  Upload, FileSpreadsheet, CheckCircle2, Sparkles
} from 'lucide-react';
import ChapterDetailPage from '@/pages/ChapterDetailPage';
import MasterLibraryPage from '@/pages/MasterLibraryPage';
import NASBrowserPage from '@/pages/NASBrowserPage';
import CloudStoragePage from '@/pages/CloudStoragePage';
import ProjectBriefEditor from '@/components/ProjectBriefEditor';

// ─── STRUCTURE BUILDER COMPONENT ─────────────────────────────
function StructureBuilder({ projectId, api, onComplete }) {
  const [chapters, setChapters] = useState([]);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState({});

  const addChapter = () => {
    const num = chapters.length + 1;
    setChapters([...chapters, {
      _key: Date.now(), number: num, title: `Chapter ${String(num).padStart(2, '0')}`,
      duration_minutes: 0, sequences: [], shots: []
    }]);
  };

  const updateChapter = (idx, field, val) => {
    setChapters(prev => prev.map((c, i) => i === idx ? { ...c, [field]: val } : c));
  };

  const removeChapter = (idx) => {
    setChapters(prev => prev.filter((_, i) => i !== idx));
  };

  const addSequence = (chIdx) => {
    setChapters(prev => prev.map((c, i) => {
      if (i !== chIdx) return c;
      const num = c.sequences.length + 1;
      return { ...c, sequences: [...c.sequences, { _key: Date.now(), number: num, name: `Sequence ${String.fromCharCode(64 + num)}`, description: '', shots: [] }] };
    }));
  };

  const updateSequence = (chIdx, seqIdx, field, val) => {
    setChapters(prev => prev.map((c, i) => {
      if (i !== chIdx) return c;
      return { ...c, sequences: c.sequences.map((s, j) => j === seqIdx ? { ...s, [field]: val } : s) };
    }));
  };

  const removeSequence = (chIdx, seqIdx) => {
    setChapters(prev => prev.map((c, i) => {
      if (i !== chIdx) return c;
      return { ...c, sequences: c.sequences.filter((_, j) => j !== seqIdx) };
    }));
  };

  const addShot = (chIdx, seqIdx = null) => {
    setChapters(prev => prev.map((c, i) => {
      if (i !== chIdx) return c;
      const allShots = [...c.shots, ...c.sequences.flatMap(s => s.shots)];
      const num = allShots.length + 1;
      const shot = { _key: Date.now(), number: num, code: '', duration_seconds: 0, complexity: 'Medium' };
      if (seqIdx !== null) {
        return { ...c, sequences: c.sequences.map((s, j) => j === seqIdx ? { ...s, shots: [...s.shots, shot] } : s) };
      }
      return { ...c, shots: [...c.shots, shot] };
    }));
  };

  const updateShot = (chIdx, seqIdx, shotIdx, field, val) => {
    setChapters(prev => prev.map((c, i) => {
      if (i !== chIdx) return c;
      if (seqIdx !== null) {
        return { ...c, sequences: c.sequences.map((s, j) => {
          if (j !== seqIdx) return s;
          return { ...s, shots: s.shots.map((sh, k) => k === shotIdx ? { ...sh, [field]: val } : sh) };
        })};
      }
      return { ...c, shots: c.shots.map((sh, k) => k === shotIdx ? { ...sh, [field]: val } : sh) };
    }));
  };

  const removeShot = (chIdx, seqIdx, shotIdx) => {
    setChapters(prev => prev.map((c, i) => {
      if (i !== chIdx) return c;
      if (seqIdx !== null) {
        return { ...c, sequences: c.sequences.map((s, j) => j !== seqIdx ? s : { ...s, shots: s.shots.filter((_, k) => k !== shotIdx) }) };
      }
      return { ...c, shots: c.shots.filter((_, k) => k !== shotIdx) };
    }));
  };

  const toggleExpand = (key) => setExpanded(p => ({ ...p, [key]: !p[key] }));

  const totalShots = chapters.reduce((s, c) => s + c.shots.length + c.sequences.reduce((s2, sq) => s2 + sq.shots.length, 0), 0);

  const saveStructure = async () => {
    setSaving(true);
    try {
      await api.post(`/projects/${projectId}/build-structure`, { chapters });
      onComplete?.();
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  return (
    <div className="space-y-4" data-testid="structure-builder">
      {/* Stats Bar */}
      <div className="flex items-center justify-between bg-zinc-900 border border-zinc-800 rounded-sm px-4 py-2.5">
        <div className="flex gap-6">
          <span className="text-[10px] text-zinc-300">Chapters: <span className="text-white font-mono font-bold">{chapters.length}</span></span>
          <span className="text-[10px] text-zinc-300">Sequences: <span className="text-white font-mono font-bold">{chapters.reduce((s, c) => s + c.sequences.length, 0)}</span></span>
          <span className="text-[10px] text-zinc-300">Shots: <span className="text-white font-mono font-bold">{totalShots}</span></span>
        </div>
        <div className="flex gap-2">
          <button data-testid="add-chapter-btn" onClick={addChapter} className="h-7 px-3 bg-blue-600 hover:bg-blue-500 text-white text-[10px] font-medium rounded-sm flex items-center gap-1.5 transition-all">
            <Plus className="w-3 h-3" /> Add Chapter
          </button>
          <button data-testid="save-structure-btn" onClick={saveStructure} disabled={saving || chapters.length === 0} className="h-7 px-3 bg-emerald-600 hover:bg-emerald-500 text-white text-[10px] font-medium rounded-sm flex items-center gap-1.5 transition-all disabled:opacity-40">
            <Save className="w-3 h-3" /> {saving ? 'Saving...' : 'Save Structure'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left: Tree View */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-3 max-h-[60vh] overflow-y-auto" data-testid="tree-view">
          <h4 className="text-[10px] text-zinc-300 uppercase tracking-wider mb-2 px-1">Project Tree</h4>
          {chapters.map((ch, ci) => (
            <div key={ch._key} className="mb-1">
              <button onClick={() => toggleExpand(`ch_${ci}`)} className="w-full flex items-center gap-1.5 px-2 py-1.5 text-xs text-zinc-300 hover:bg-zinc-800/50 rounded-sm transition-colors">
                {expanded[`ch_${ci}`] ? <ChevronDown className="w-3 h-3 text-cyan-400" /> : <ChevronRight className="w-3 h-3 text-cyan-400" />}
                <Layers className="w-3 h-3 text-cyan-400" />
                <span className="truncate">{ch.title || `Chapter ${ch.number}`}</span>
                <span className="ml-auto text-[9px] text-zinc-400">{ch.sequences.reduce((s, sq) => s + sq.shots.length, 0) + ch.shots.length} shots</span>
              </button>
              {expanded[`ch_${ci}`] && (
                <div className="ml-4 border-l border-zinc-800 pl-2">
                  {ch.sequences.map((seq, si) => (
                    <div key={seq._key} className="mb-0.5">
                      <button onClick={() => toggleExpand(`seq_${ci}_${si}`)} className="w-full flex items-center gap-1.5 px-2 py-1 text-[11px] text-zinc-400 hover:bg-zinc-800/30 rounded-sm">
                        {expanded[`seq_${ci}_${si}`] ? <ChevronDown className="w-2.5 h-2.5" /> : <ChevronRight className="w-2.5 h-2.5" />}
                        <FolderOpen className="w-2.5 h-2.5 text-violet-400" />
                        <span className="truncate">{seq.name}</span>
                      </button>
                      {expanded[`seq_${ci}_${si}`] && seq.shots.map((shot, shi) => (
                        <div key={shot._key} className="ml-4 flex items-center gap-1.5 px-2 py-0.5 text-[10px] text-zinc-500">
                          <Clapperboard className="w-2.5 h-2.5 text-amber-400" />
                          <span>{shot.code || `Shot ${shot.number}`}</span>
                        </div>
                      ))}
                    </div>
                  ))}
                  {ch.shots.map((shot, shi) => (
                    <div key={shot._key} className="flex items-center gap-1.5 px-2 py-0.5 text-[10px] text-zinc-500">
                      <Clapperboard className="w-2.5 h-2.5 text-amber-400" />
                      <span>{shot.code || `Shot ${shot.number}`}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
          {chapters.length === 0 && <p className="text-[10px] text-zinc-400 text-center py-6">Click "Add Chapter" to start building</p>}
        </div>

        {/* Right: Editor */}
        <div className="lg:col-span-2 space-y-3 max-h-[60vh] overflow-y-auto pr-1">
          {chapters.map((ch, ci) => (
            <div key={ch._key} className="bg-zinc-900 border border-zinc-800 rounded-sm" data-testid={`chapter-${ci}`}>
              {/* Chapter Header */}
              <div className="px-4 py-3 border-b border-zinc-800 flex items-center gap-3">
                <Layers className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                <div className="flex-1 grid grid-cols-4 gap-2">
                  <div>
                    <label className="text-[8px] text-zinc-400 uppercase">Number</label>
                    <input type="number" value={ch.number} onChange={e => updateChapter(ci, 'number', parseInt(e.target.value) || 1)} className="w-full h-7 bg-zinc-950 border border-zinc-800 rounded-sm px-2 text-xs text-white focus:outline-none focus:ring-1 focus:ring-cyan-500" />
                  </div>
                  <div className="col-span-2">
                    <label className="text-[8px] text-zinc-400 uppercase">Title</label>
                    <input value={ch.title} onChange={e => updateChapter(ci, 'title', e.target.value)} className="w-full h-7 bg-zinc-950 border border-zinc-800 rounded-sm px-2 text-xs text-white focus:outline-none focus:ring-1 focus:ring-cyan-500" />
                  </div>
                  <div>
                    <label className="text-[8px] text-zinc-400 uppercase">Duration (min)</label>
                    <input type="number" value={ch.duration_minutes} onChange={e => updateChapter(ci, 'duration_minutes', parseFloat(e.target.value) || 0)} className="w-full h-7 bg-zinc-950 border border-zinc-800 rounded-sm px-2 text-xs text-white focus:outline-none focus:ring-1 focus:ring-cyan-500" />
                  </div>
                </div>
                <button onClick={() => removeChapter(ci)} className="text-zinc-600 hover:text-red-400 transition-colors flex-shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>

              {/* Sequences */}
              <div className="px-4 py-2 space-y-2">
                {ch.sequences.map((seq, si) => (
                  <div key={seq._key} className="bg-zinc-800/30 border border-zinc-800/50 rounded-sm p-3" data-testid={`sequence-${ci}-${si}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <FolderOpen className="w-3.5 h-3.5 text-violet-400 flex-shrink-0" />
                      <input value={seq.name} onChange={e => updateSequence(ci, si, 'name', e.target.value)} className="flex-1 h-6 bg-zinc-950 border border-zinc-800 rounded-sm px-2 text-[11px] text-white focus:outline-none focus:ring-1 focus:ring-violet-500" placeholder="Sequence name" />
                      <input value={seq.description} onChange={e => updateSequence(ci, si, 'description', e.target.value)} className="flex-1 h-6 bg-zinc-950 border border-zinc-800 rounded-sm px-2 text-[11px] text-zinc-400 focus:outline-none" placeholder="Description (optional)" />
                      <button onClick={() => removeSequence(ci, si)} className="text-zinc-600 hover:text-red-400"><Trash2 className="w-3 h-3" /></button>
                    </div>
                    {/* Shots in sequence */}
                    <div className="space-y-1 ml-5">
                      {seq.shots.map((shot, shi) => (
                        <div key={shot._key} className="flex items-center gap-2" data-testid={`shot-${ci}-${si}-${shi}`}>
                          <Clapperboard className="w-3 h-3 text-amber-400 flex-shrink-0" />
                          <input value={shot.code} onChange={e => updateShot(ci, si, shi, 'code', e.target.value)} className="w-24 h-6 bg-zinc-950 border border-zinc-800 rounded-sm px-2 text-[10px] font-mono text-white focus:outline-none" placeholder="Shot code" />
                          <input type="number" value={shot.duration_seconds} onChange={e => updateShot(ci, si, shi, 'duration_seconds', parseFloat(e.target.value) || 0)} className="w-16 h-6 bg-zinc-950 border border-zinc-800 rounded-sm px-2 text-[10px] text-zinc-400 focus:outline-none" placeholder="Sec" />
                          <select value={shot.complexity} onChange={e => updateShot(ci, si, shi, 'complexity', e.target.value)} className="h-6 bg-zinc-950 border border-zinc-800 rounded-sm px-1 text-[10px] text-zinc-400 focus:outline-none">
                            {['Low', 'Medium', 'High', 'Very High'].map(c => <option key={c} value={c}>{c}</option>)}
                          </select>
                          <button onClick={() => removeShot(ci, si, shi)} className="text-zinc-700 hover:text-red-400"><Trash2 className="w-2.5 h-2.5" /></button>
                        </div>
                      ))}
                      <button onClick={() => addShot(ci, si)} className="text-[9px] text-zinc-400 hover:text-amber-400 flex items-center gap-1 mt-1 transition-colors">
                        <Plus className="w-2.5 h-2.5" /> Add Shot
                      </button>
                    </div>
                  </div>
                ))}

                {/* Shots directly under chapter */}
                {ch.shots.map((shot, shi) => (
                  <div key={shot._key} className="flex items-center gap-2 ml-1">
                    <Clapperboard className="w-3 h-3 text-amber-400 flex-shrink-0" />
                    <input value={shot.code} onChange={e => updateShot(ci, null, shi, 'code', e.target.value)} className="w-24 h-6 bg-zinc-950 border border-zinc-800 rounded-sm px-2 text-[10px] font-mono text-white focus:outline-none" placeholder="Shot code" />
                    <input type="number" value={shot.duration_seconds} onChange={e => updateShot(ci, null, shi, 'duration_seconds', parseFloat(e.target.value) || 0)} className="w-16 h-6 bg-zinc-950 border border-zinc-800 rounded-sm px-2 text-[10px] text-zinc-400 focus:outline-none" placeholder="Sec" />
                    <select value={shot.complexity} onChange={e => updateShot(ci, null, shi, 'complexity', e.target.value)} className="h-6 bg-zinc-950 border border-zinc-800 rounded-sm px-1 text-[10px] text-zinc-400 focus:outline-none">
                      {['Low', 'Medium', 'High', 'Very High'].map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <button onClick={() => removeShot(ci, null, shi)} className="text-zinc-700 hover:text-red-400"><Trash2 className="w-2.5 h-2.5" /></button>
                  </div>
                ))}

                {/* Action Buttons */}
                <div className="flex gap-2 pt-1">
                  <button onClick={() => addSequence(ci)} className="text-[9px] text-zinc-400 hover:text-violet-400 flex items-center gap-1 transition-colors">
                    <Plus className="w-2.5 h-2.5" /> Add Sequence
                  </button>
                  <button onClick={() => addShot(ci)} className="text-[9px] text-zinc-500 hover:text-amber-400 flex items-center gap-1 transition-colors">
                    <Plus className="w-2.5 h-2.5" /> Add Shot (no sequence)
                  </button>
                </div>
              </div>
            </div>
          ))}

          {chapters.length === 0 && (
            <div className="bg-zinc-900 border border-zinc-800 border-dashed rounded-sm p-12 text-center">
              <Layers className="w-10 h-10 text-zinc-800 mx-auto mb-3" />
              <p className="text-sm text-zinc-300">No chapters yet</p>
              <p className="text-xs text-zinc-400 mt-1">Start by adding chapters, then add sequences and shots inside them</p>
              <button onClick={addChapter} className="mt-4 h-8 px-5 bg-blue-600 hover:bg-blue-500 text-white text-xs font-medium rounded-sm inline-flex items-center gap-2">
                <Plus className="w-3.5 h-3.5" /> Add First Chapter
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ─── MAIN PROJECTS PAGE ──────────────────────────────────────
export default function ProjectsPage() {
  const { api, user } = useAuth();
  const location = useLocation();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [episodes, setEpisodes] = useState([]);
  const [sequences, setSequences] = useState([]);
  const [shots, setShots] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showBuilder, setShowBuilder] = useState(false);
  const [activeChapter, setActiveChapter] = useState(null);
  const [projectTab, setProjectTab] = useState('chapters'); // chapters | master_library
  const [form, setForm] = useState({
    name: '', client_name: '', description: '', budget: '', deadline: '',
    project_type: 'series', fps: 24, resolution: '1920x1080', total_duration_minutes: '',
    create_as_show: false,
  });

  const loadProjects = useCallback(() => {
    api.get('/projects').then(r => setProjects(r.data));
  }, [api]);

  useEffect(() => { loadProjects(); }, [loadProjects]);

  // When the user re-clicks "Projects" in the sidebar, react-router pushes
  // a fresh history entry with a new `location.key` even if the pathname
  // stays the same. Reset the detail view so they always land on the grid.
  useEffect(() => {
    setSelectedProject(null);
    setActiveChapter(null);
    setShowBuilder(false);
    /* eslint-disable-next-line */
  }, [location.key]);

  useEffect(() => {
    if (!selectedProject) return;
    Promise.all([
      api.get(`/episodes?project_id=${selectedProject.id}`),
      api.get(`/sequences?project_id=${selectedProject.id}`),
      api.get(`/shots?project_id=${selectedProject.id}`),
    ]).then(([e, sq, s]) => { setEpisodes(e.data); setSequences(sq.data); setShots(s.data); });
  }, [selectedProject, api]);

  const createProject = async () => {
    const payload = {
      ...form, budget: parseFloat(form.budget) || 0,
      fps: parseInt(form.fps) || 24,
      total_duration_minutes: parseFloat(form.total_duration_minutes) || 0
    };
    delete payload.create_as_show;
    const res = await api.post('/projects', payload);
    let created = res.data;
    // If user wants this to be a multi-episode SHOW, immediately convert it.
    // Behaviour: skip the "Episode 01" auto-spawn (that's for existing flat projects);
    // just promote to Show so the user can add Episodes themselves.
    if (form.create_as_show) {
      try {
        await api.put(`/projects/${created.id}`, { is_show: true });
        created = { ...created, is_show: true };
      } catch (err) { /* fall back to convert-to-show endpoint if PUT not supported */
        try {
          const conv = await api.post(`/projects/${created.id}/convert-to-show`, { episode_name: 'Episode 01' });
          created = { ...created, is_show: true, _first_ep: conv.data?.first_episode_id };
        } catch (e) { /* keep flat if both fail */ }
      }
    }
    setProjects([...projects, created]);
    setSelectedProject(created);
    setShowCreate(false);
    // For SHOW: skip Structure Builder (Shows are containers), open the
    // Add-Episode dialog so the user can create their first episode immediately.
    if (form.create_as_show) {
      setShowAddEpisode(true);
      setEpisodeForm({ name: 'Episode 01', deadline: form.deadline || '' });
    } else {
      setShowBuilder(true);  // Open structure builder for flat projects
    }
    setForm({ name: '', client_name: '', description: '', budget: '', deadline: '', project_type: 'series', fps: 24, resolution: '1920x1080', total_duration_minutes: '', create_as_show: false });
  };

  const onStructureBuilt = () => {
    setShowBuilder(false);
    loadProjects();
    if (selectedProject) {
      Promise.all([
        api.get(`/episodes?project_id=${selectedProject.id}`),
        api.get(`/sequences?project_id=${selectedProject.id}`),
        api.get(`/shots?project_id=${selectedProject.id}`),
      ]).then(([e, sq, s]) => { setEpisodes(e.data); setSequences(sq.data); setShots(s.data); });
    }
  };

  // ─── Delete · Branching · Episodes ────────────────────────
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showAddEpisode, setShowAddEpisode] = useState(false);
  const [episodeForm, setEpisodeForm] = useState({ name: '', deadline: '' });
  const [showConvert, setShowConvert] = useState(false);
  const [convertForm, setConvertForm] = useState({ episode_name: 'Episode 01' });
  const [showsExpanded, setShowsExpanded] = useState({});
  const [showEpisodes, setShowEpisodes] = useState({}); // {showId: [eps]}

  const isAdmin = user?.role === 'master_admin';

  const loadShowEpisodes = (showId) => {
    api.get(`/projects/${showId}/episodes`).then(r => setShowEpisodes(s => ({ ...s, [showId]: r.data.episodes })));
  };

  const doDelete = async (project, confirm) => {
    try {
      await api.delete(`/projects/${project.id}`, { data: { confirm } });
      setDeleteTarget(null);
      if (selectedProject?.id === project.id) setSelectedProject(null);
      loadProjects();
    } catch (e) { alert(e.response?.data?.detail || 'Delete failed'); }
  };
  const doConvert = async () => {
    try {
      await api.post(`/projects/${selectedProject.id}/convert-to-show`, convertForm);
      setShowConvert(false);
      loadProjects();
      // Refresh selected project
      const r = await api.get('/projects');
      const updated = r.data.find(p => p.id === selectedProject.id);
      setSelectedProject(updated);
    } catch (e) { alert(e.response?.data?.detail || 'Convert failed'); }
  };
  const doAddEpisode = async () => {
    try {
      await api.post(`/projects/${selectedProject.id}/episodes`, episodeForm);
      setShowAddEpisode(false);
      setEpisodeForm({ name: '', deadline: '' });
      loadShowEpisodes(selectedProject.id);
      loadProjects();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };
  // When a show is selected, auto-load its episodes
  useEffect(() => {
    if (selectedProject?.is_show) loadShowEpisodes(selectedProject.id);
    /* eslint-disable-next-line */
  }, [selectedProject?.id, selectedProject?.is_show]);


  return (
    <div className="space-y-5" data-testid="projects-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-white">Projects</h1>
          <p className="text-xs text-zinc-500 mt-1">Create projects, define framework, build structure</p>
        </div>
        <button data-testid="create-project-btn" onClick={() => setShowCreate(true)} className="h-8 px-4 bg-blue-600 hover:bg-blue-500 text-white text-xs font-medium rounded-sm flex items-center gap-2 transition-all">
          <Plus className="w-3.5 h-3.5" /> New Project
        </button>
      </div>

      {/* ── Create Project Modal ── */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" data-testid="create-project-modal">
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6 w-[32rem]">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-sm font-heading font-semibold text-white">Create New Project</h3>
              <button onClick={() => setShowCreate(false)} className="text-zinc-600 hover:text-zinc-300"><X className="w-4 h-4" /></button>
            </div>

            {/* Heads-up: avoid duplicate creation paths */}
            <div className="mb-4 p-2.5 bg-amber-500/5 border border-amber-500/20 rounded-sm flex items-start gap-2 text-[10px] text-amber-200">
              <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <p>
                <span className="font-medium">Quick project only.</span> If you also need to create a new <span className="text-amber-300">client</span> with login + brief + access grant in one go, use the <span className="text-amber-300">"New Client"</span> wizard from the sidebar instead.
              </p>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-[10px] text-zinc-500 uppercase tracking-wider">Project Name *</label>
                <input data-testid="create-project-name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full h-9 bg-zinc-950 border border-zinc-800 rounded-sm px-3 text-sm text-white focus:ring-1 focus:ring-blue-500 focus:outline-none" placeholder="e.g., The Science, The Magic" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] text-zinc-500 uppercase tracking-wider">Client Name</label>
                  <input data-testid="create-project-client" value={form.client_name} onChange={e => setForm({...form, client_name: e.target.value})} className="w-full h-8 bg-zinc-950 border border-zinc-800 rounded-sm px-3 text-xs text-white focus:ring-1 focus:ring-blue-500 focus:outline-none" />
                </div>
                <div>
                  <label className="text-[10px] text-zinc-500 uppercase tracking-wider">Project Type *</label>
                  <select data-testid="create-project-type" value={form.project_type} onChange={e => setForm({...form, project_type: e.target.value})} className="w-full h-8 bg-zinc-950 border border-zinc-800 rounded-sm px-2 text-xs text-zinc-300 focus:ring-1 focus:ring-blue-500 focus:outline-none">
                    <option value="series">Series</option>
                    <option value="film">Film</option>
                    <option value="ad">Ad / Commercial</option>
                    <option value="short">Short Film</option>
                    <option value="music_video">Music Video</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-[10px] text-zinc-500 uppercase tracking-wider flex items-center gap-1"><Monitor className="w-3 h-3" /> FPS</label>
                  <select value={form.fps} onChange={e => setForm({...form, fps: e.target.value})} className="w-full h-8 bg-zinc-950 border border-zinc-800 rounded-sm px-2 text-xs text-zinc-300 focus:outline-none">
                    {[12, 15, 24, 25, 30, 48, 60].map(f => <option key={f} value={f}>{f} fps</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] text-zinc-500 uppercase tracking-wider flex items-center gap-1"><Settings2 className="w-3 h-3" /> Resolution</label>
                  <select value={form.resolution} onChange={e => setForm({...form, resolution: e.target.value})} className="w-full h-8 bg-zinc-950 border border-zinc-800 rounded-sm px-2 text-xs text-zinc-300 focus:outline-none">
                    {['1280x720', '1920x1080', '2048x1080', '2560x1440', '3840x2160', '4096x2160'].map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] text-zinc-500 uppercase tracking-wider flex items-center gap-1"><Clock className="w-3 h-3" /> Duration (min)</label>
                  <input type="number" value={form.total_duration_minutes} onChange={e => setForm({...form, total_duration_minutes: e.target.value})} className="w-full h-8 bg-zinc-950 border border-zinc-800 rounded-sm px-2 text-xs text-white focus:outline-none" placeholder="90" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] text-zinc-500 uppercase tracking-wider">Budget (INR)</label>
                  <input data-testid="create-project-budget" value={form.budget} onChange={e => setForm({...form, budget: e.target.value})} className="w-full h-8 bg-zinc-950 border border-zinc-800 rounded-sm px-3 text-xs text-white focus:outline-none" placeholder="1800000" />
                </div>
                <div>
                  <label className="text-[10px] text-zinc-500 uppercase tracking-wider">Deadline</label>
                  <input type="date" value={form.deadline} onChange={e => setForm({...form, deadline: e.target.value})} className="w-full h-8 bg-zinc-950 border border-zinc-800 rounded-sm px-3 text-xs text-zinc-300 focus:outline-none" />
                </div>
              </div>

              <div>
                <label className="text-[10px] text-zinc-500 uppercase tracking-wider">Description</label>
                <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={2} className="w-full bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2 text-xs text-white focus:outline-none" placeholder="Brief project description..." />
              </div>

              {/* Show vs Episode toggle */}
              <label className="flex items-start gap-3 p-3 rounded-sm bg-violet-500/5 border border-violet-500/30 cursor-pointer hover:bg-violet-500/10 transition" data-testid="create-as-show-toggle">
                <input type="checkbox" checked={form.create_as_show}
                  onChange={e => setForm({ ...form, create_as_show: e.target.checked })}
                  className="mt-0.5 accent-violet-500" />
                <div className="flex-1">
                  <p className="text-xs text-violet-200 font-medium">Multi-episode Show (anthology / series)</p>
                  <p className="text-[10px] text-zinc-400 mt-0.5">
                    Check this if the project will hold multiple Episodes (e.g. <span className="text-violet-300">Fairy Tales → Hansel &amp; Gretel · Cinderella · Thumbelina</span>).
                    Each episode gets its own MDL, screenplay upload and shot list — completely isolated from siblings.
                    Leave unchecked for a single film / ad / one-off.
                  </p>
                </div>
              </label>
            </div>

            <div className="flex gap-2 mt-5">
              <button data-testid="create-project-submit" onClick={createProject} disabled={!form.name} className="flex-1 h-9 bg-blue-600 hover:bg-blue-500 text-white text-xs font-medium rounded-sm transition-all disabled:opacity-40 flex items-center justify-center gap-2">
                {form.create_as_show ? 'Create Show & Add Episode' : 'Create & Build Structure'} <ChevronRight className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => setShowCreate(false)} className="w-24 h-9 bg-zinc-800 text-zinc-400 text-xs rounded-sm">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Structure Builder Mode ── */}
      {showBuilder && selectedProject && (
        <div className="bg-zinc-950 border border-blue-500/30 rounded-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-heading font-bold text-white">Structure Builder — {selectedProject.name}</h2>
              <p className="text-xs text-zinc-500 mt-0.5">
                {selectedProject.project_type} | {selectedProject.fps} fps | {selectedProject.resolution}
                {selectedProject.total_duration_minutes ? ` | ${selectedProject.total_duration_minutes} min` : ''}
              </p>
            </div>
            <button onClick={() => { setShowBuilder(false); onStructureBuilt(); }} className="h-7 px-3 bg-zinc-800 text-zinc-400 text-[10px] rounded-sm hover:text-white flex items-center gap-1">
              <X className="w-3 h-3" /> Close Builder
            </button>
          </div>
          <StructureBuilder projectId={selectedProject.id} api={api} onComplete={onStructureBuilt} />
        </div>
      )}

      {/* ── Chapter Detail View ── */}
      {activeChapter && selectedProject && !showBuilder && (
        <ChapterDetailPage
          chapterId={activeChapter.id}
          projectId={selectedProject.id}
          chapterName={activeChapter.name}
          onBack={() => setActiveChapter(null)}
        />
      )}

      {/* ── Project List + Detail ── */}
      {!showBuilder && !activeChapter && (
        <>
          {/* Back to All Projects — sole, clear navigation control */}
          {selectedProject ? (
            <div className="flex items-center justify-between bg-zinc-900 border border-zinc-800 rounded-sm px-4 py-2.5"
                 data-testid="projects-detail-breadcrumb">
              <button onClick={() => setSelectedProject(null)} data-testid="back-to-all-projects"
                className="flex items-center gap-2 text-xs text-zinc-300 hover:text-white transition-colors">
                <ArrowLeft className="w-3.5 h-3.5" />
                <span>Back to All Projects</span>
              </button>
              <div className="flex items-center gap-2 text-[11px]">
                <span className="text-zinc-500">All Projects</span>
                <ChevronRight className="w-3 h-3 text-zinc-700" />
                {selectedProject.parent_project_id && (
                  <>
                    <button
                      onClick={() => {
                        const parent = projects.find(x => x.id === selectedProject.parent_project_id);
                        if (parent) setSelectedProject(parent);
                      }}
                      className="text-violet-300 hover:text-violet-200 transition-colors">
                      {selectedProject.show_name || 'Show'}
                    </button>
                    <ChevronRight className="w-3 h-3 text-zinc-700" />
                  </>
                )}
                <span className="text-white font-medium">{selectedProject.name}</span>
                {selectedProject.is_show && (
                  <span className="ml-1 px-1.5 py-0.5 text-[9px] uppercase rounded bg-violet-500/20 text-violet-300">show</span>
                )}
                {selectedProject.parent_project_id && (
                  <span className="ml-1 px-1.5 py-0.5 text-[9px] uppercase rounded bg-cyan-500/20 text-cyan-300">episode</span>
                )}
              </div>
            </div>
          ) : null}

          {/* Full project list (only when NO project selected) */}
          {!selectedProject ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {projects.filter(p => !p.parent_project_id).map(p => {
                const progress = p.shot_count > 0 ? Math.round((p.completed_shots / p.shot_count) * 100) : 0;
                const eps = projects.filter(x => x.parent_project_id === p.id);
                return (
                  <div key={p.id} data-testid={`project-card-${p.id}`}
                    className="bg-zinc-900 border border-zinc-800 rounded-sm p-4 text-left hover:border-zinc-700 transition-all group relative">
                    <div className="absolute top-2 right-2 flex items-center gap-1">
                      {p.is_show && <span className="text-[8px] uppercase px-1.5 py-0.5 rounded bg-violet-500/20 text-violet-300 flex items-center gap-1"><Tv className="w-2 h-2" /> SHOW</span>}
                      {isAdmin && (
                        <button onClick={(e) => { e.stopPropagation(); setDeleteTarget(p); }}
                          data-testid={`delete-project-${p.id}`}
                          className="p-1 text-zinc-700 hover:text-rose-400 transition-colors">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                    <button onClick={() => setSelectedProject(p)} data-testid={`project-select-${p.id}`} className="w-full text-left">
                      <div className="flex items-center gap-2 mb-1 pr-12">
                        <p className="text-sm font-medium text-zinc-200 group-hover:text-blue-400 transition-colors">{p.name}</p>
                      </div>
                      <p className="text-[10px] text-zinc-600">{p.client_name} | {p.project_type || 'series'}</p>
                      {p.is_show ? (
                        <p className="text-[10px] text-violet-400 mt-2 flex items-center gap-1">
                          <GitBranch className="w-3 h-3" /> {eps.length} episode{eps.length !== 1 ? 's' : ''}
                        </p>
                      ) : (
                        <>
                          <div className="flex items-center gap-2 mt-2">
                            <div className="flex-1 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                              <div className="h-full bg-blue-500 rounded-full" style={{ width: `${progress}%` }} />
                            </div>
                            <span className="text-[10px] font-mono text-zinc-500">{progress}%</span>
                          </div>
                          <div className="flex gap-3 mt-2 text-[10px] text-zinc-600">
                            <span>{p.shot_count || 0} shots</span>
                            <span>{p.completed_shots || 0} done</span>
                          </div>
                        </>
                      )}
                    </button>
                    {p.is_show && eps.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-zinc-800 space-y-1" data-testid={`episodes-list-${p.id}`}>
                        {eps.map(ep => (
                          <button key={ep.id} onClick={() => setSelectedProject(ep)} data-testid={`episode-${ep.id}`}
                            className="w-full flex items-center gap-1.5 px-2 py-1 text-[10px] text-zinc-400 hover:text-white hover:bg-zinc-800/50 rounded">
                            <span className="text-zinc-600">EP{String(ep.episode_number || 0).padStart(2, '0')}</span>
                            <span className="flex-1 text-left truncate">{ep.name}</span>
                            {ep.shot_count > 0 && <span className="text-[9px] text-amber-400">{ep.shot_count}sh</span>}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
          <div className="space-y-4">
            {/* Detail View — Full Width when project selected */}
                {/* Project Header */}
                <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h2 className="text-lg font-heading font-bold text-white">{selectedProject.name}</h2>
                      <p className="text-xs text-zinc-400">
                        {selectedProject.client_name} | {selectedProject.project_type || 'series'} | {selectedProject.fps || 24} fps | {selectedProject.resolution || '1920x1080'}
                        {selectedProject.total_duration_minutes ? ` | ${selectedProject.total_duration_minutes} min` : ''}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      {isAdmin && !selectedProject.parent_project_id && !selectedProject.is_show && (
                        <button onClick={() => setShowConvert(true)} data-testid="convert-to-show-btn"
                          className="h-7 px-3 bg-violet-600 hover:bg-violet-500 text-white text-[10px] font-medium rounded-sm flex items-center gap-1.5">
                          <Tv className="w-3 h-3" /> Convert to Show
                        </button>
                      )}
                      {isAdmin && selectedProject.is_show && (
                        <button onClick={() => setShowAddEpisode(true)} data-testid="add-episode-btn"
                          className="h-7 px-3 bg-violet-600 hover:bg-violet-500 text-white text-[10px] font-medium rounded-sm flex items-center gap-1.5">
                          <GitBranch className="w-3 h-3" /> + Episode
                        </button>
                      )}
                      <button data-testid="open-builder-btn" onClick={() => setShowBuilder(true)} className="h-7 px-3 bg-cyan-600 hover:bg-cyan-500 text-white text-[10px] font-medium rounded-sm flex items-center gap-1.5">
                        <Layers className="w-3 h-3" /> Structure Builder
                      </button>
                      {isAdmin && (
                        <button onClick={() => setDeleteTarget(selectedProject)} data-testid="delete-project-detail-btn"
                          className="h-7 px-3 bg-rose-600/30 hover:bg-rose-500/40 text-rose-200 text-[10px] font-medium rounded-sm flex items-center gap-1.5 border border-rose-500/30">
                          <Trash2 className="w-3 h-3" /> Delete
                        </button>
                      )}
                      <span className={`status-${selectedProject.status} px-2 py-1 rounded-full text-[10px] font-medium`}>{selectedProject.status}</span>
                    </div>
                  </div>
                  <p className="text-xs text-zinc-400 mt-2">{selectedProject.description}</p>

                  {/* Client linkage strip — only visible for top-level projects (not episodes,
                      since they inherit their show's link). Show current link or open the picker. */}
                  {!selectedProject.parent_project_id && isAdmin && (
                    <ClientLinkStrip
                      api={api}
                      project={selectedProject}
                      onChanged={(updated) => {
                        setSelectedProject(prev => ({ ...prev, ...updated }));
                        loadProjects();
                      }}
                    />
                  )}

                  <div className="grid grid-cols-4 gap-3 mt-4">
                    <div className="bg-zinc-800/50 rounded-sm p-3 text-center">
                      <p className="text-lg font-heading font-bold text-cyan-400">{episodes.length}</p>
                      <p className="text-[10px] text-zinc-400">Chapters</p>
                    </div>
                    <div className="bg-zinc-800/50 rounded-sm p-3 text-center">
                      <p className="text-lg font-heading font-bold text-violet-400">{sequences.length}</p>
                      <p className="text-[10px] text-zinc-400">Sequences</p>
                    </div>
                    <div className="bg-zinc-800/50 rounded-sm p-3 text-center">
                      <p className="text-lg font-heading font-bold text-amber-400">{shots.length}</p>
                      <p className="text-[10px] text-zinc-400">Shots</p>
                    </div>
                    <div className="bg-zinc-800/50 rounded-sm p-3 text-center">
                      <p className="text-lg font-heading font-bold text-emerald-400">{selectedProject.completed_shots || 0}</p>
                      <p className="text-[10px] text-zinc-400">Completed</p>
                    </div>
                  </div>
                </div>

                {/* For Shows: render an Episode picker instead of the Master Library /
                    Cloud Storage tabs (a Show is a container, not a workspace). */}
                {selectedProject.is_show ? (
                  <ShowEpisodePicker
                    show={selectedProject}
                    api={api}
                    episodes={projects
                      .filter(x => x.parent_project_id === selectedProject.id)
                      .sort((a, b) => {
                        const an = a.episode_number ?? 9999;
                        const bn = b.episode_number ?? 9999;
                        if (an !== bn) return an - bn;
                        return (a.created_at || '').localeCompare(b.created_at || '');
                      })}
                    onPick={(ep) => setSelectedProject(ep)}
                    onAddEpisode={() => setShowAddEpisode(true)}
                    isAdmin={isAdmin}
                    onImportComplete={() => loadProjects()}
                  />
                ) : (
                <>
                {/* Tab Switcher: Brief / Master Library / Chapters / NAS / Cloud */}
                <div className="flex gap-1 border-b border-zinc-800 flex-wrap">
                  <button data-testid="tab-project-brief" onClick={() => setProjectTab('brief')}
                    className={`px-4 py-2 text-xs font-medium border-b-2 -mb-px flex items-center gap-1.5 transition-colors ${projectTab === 'brief' ? 'border-violet-500 text-white' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}>
                    <Sparkles className="w-3.5 h-3.5" /> Brief & References
                  </button>
                  <button data-testid="tab-master-library" onClick={() => setProjectTab('master_library')}
                    className={`px-4 py-2 text-xs font-medium border-b-2 -mb-px flex items-center gap-1.5 transition-colors ${projectTab === 'master_library' ? 'border-violet-500 text-white' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}>
                    <Database className="w-3.5 h-3.5" /> Master Data Library
                  </button>
                  <button data-testid="tab-chapters" onClick={() => setProjectTab('chapters')}
                    className={`px-4 py-2 text-xs font-medium border-b-2 -mb-px flex items-center gap-1.5 transition-colors ${projectTab === 'chapters' ? 'border-cyan-500 text-white' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}>
                    <BookOpen className="w-3.5 h-3.5" /> Chapters ({episodes.length})
                  </button>
                  <button data-testid="tab-nas-storage" onClick={() => setProjectTab('nas_storage')}
                    className={`px-4 py-2 text-xs font-medium border-b-2 -mb-px flex items-center gap-1.5 transition-colors ${projectTab === 'nas_storage' ? 'border-amber-500 text-white' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}>
                    <HardDrive className="w-3.5 h-3.5" /> NAS Storage
                  </button>
                  <button data-testid="tab-cloud-storage" onClick={() => setProjectTab('cloud_storage')}
                    className={`px-4 py-2 text-xs font-medium border-b-2 -mb-px flex items-center gap-1.5 transition-colors ${projectTab === 'cloud_storage' ? 'border-cyan-500 text-white' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}>
                    <Cloud className="w-3.5 h-3.5" /> Cloud Storage
                  </button>
                </div>

                {/* Brief & References Tab (ONE DATA > MANY ACCESS) */}
                {projectTab === 'brief' && (
                  <ProjectBriefEditor
                    projectId={selectedProject.id}
                    projectName={selectedProject.name}
                    api={api}
                    canEdit={['master_admin', 'production_manager', 'supervisor',
                              'creative_director', 'producer', 'line_producer',
                              'executive_producer', 'screenwriter', 'story_artist',
                              'storyboard_artist'].includes(user?.role)}
                  />
                )}

                {/* Master Library Tab */}
                {projectTab === 'master_library' && (
                  <MasterLibraryPage projectId={selectedProject.id} projectName={selectedProject.name} api={api} />
                )}

                {/* NAS Storage Tab */}
                {projectTab === 'nas_storage' && (
                  <NASBrowserPage projectId={selectedProject.id} projectName={selectedProject.name} api={api} />
                )}

                {/* Cloud Storage Tab */}
                {projectTab === 'cloud_storage' && (
                  <CloudStoragePage projectId={selectedProject.id} projectName={selectedProject.name} />
                )}

                {/* Chapters Tab */}
                {projectTab === 'chapters' && (<>
                <div className="bg-zinc-900 border border-zinc-800 rounded-sm">
                  <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
                    <h3 className="text-xs font-semibold text-zinc-300 uppercase tracking-wider">Chapters ({episodes.length})</h3>
                    <button onClick={async () => {
                      const name = prompt(`Chapter name (e.g., "Fairy Tale 1 - Cinderella"):`);
                      if (!name) return;
                      const num = episodes.length + 1;
                      await api.post('/episodes', { project_id: selectedProject.id, name: name, number: num, status: 'not_started' });
                      const e = await api.get(`/episodes?project_id=${selectedProject.id}`);
                      setEpisodes(e.data);
                    }} className="h-7 px-3 bg-cyan-600 hover:bg-cyan-500 text-white text-[10px] font-medium rounded-sm flex items-center gap-1">
                      <Plus className="w-3 h-3" /> Add Chapter
                    </button>
                  </div>
                  <div className="divide-y divide-zinc-800/30">
                    {episodes.map(ep => {
                      const epSeqs = sequences.filter(s => s.episode_id === ep.id);
                      const epShots = shots.filter(s => s.episode_id === ep.id);
                      const epDone = epShots.filter(s => s.status === 'completed').length;
                      return (
                        <button key={ep.id} className="w-full text-left px-4 py-3 hover:bg-zinc-800/30 transition-colors group" data-testid={`episode-${ep.id}`}
                          onClick={() => setActiveChapter(ep)}>
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-xs text-zinc-200 font-medium group-hover:text-cyan-400 transition-colors">{ep.name}</p>
                              <p className="text-[10px] text-zinc-600">
                                {epSeqs.length} sequences | {epShots.length} shots | {epDone} completed
                                {ep.duration_minutes ? ` | ${ep.duration_minutes} min` : ''}
                              </p>
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="w-20 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                                <div className="h-full bg-cyan-500 rounded-full" style={{ width: `${epShots.length > 0 ? (epDone / epShots.length * 100) : 0}%` }} />
                              </div>
                              <span className={`status-${ep.status} px-1.5 py-0.5 rounded-full text-[9px] font-medium`}>{ep.status?.replace('_', ' ')}</span>
                              <ChevronRight className="w-3 h-3 text-zinc-600 group-hover:text-cyan-400" />
                            </div>
                          </div>
                        </button>
                      );
                    })}
                    {episodes.length === 0 && (
                      <div className="px-4 py-8 text-center">
                        <p className="text-xs text-zinc-600 mb-2">No structure defined yet</p>
                        <button onClick={() => setShowBuilder(true)} className="h-7 px-4 bg-cyan-600 hover:bg-cyan-500 text-white text-[10px] font-medium rounded-sm inline-flex items-center gap-1.5">
                          <Layers className="w-3 h-3" /> Open Structure Builder
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Shots Table */}
                {shots.length > 0 && (
                  <div className="bg-zinc-900 border border-zinc-800 rounded-sm">
                    <div className="px-4 py-3 border-b border-zinc-800">
                      <h3 className="text-xs font-semibold text-zinc-300 uppercase tracking-wider">Shots ({shots.length})</h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full" data-testid="shots-table">
                        <thead>
                          <tr className="border-b border-zinc-800/50 text-[10px] text-zinc-500 uppercase">
                            <th className="text-left px-4 py-2 font-medium">Shot</th>
                            <th className="text-left px-4 py-2 font-medium">Complexity</th>
                            <th className="text-left px-4 py-2 font-medium">Duration</th>
                            <th className="text-left px-4 py-2 font-medium">Status</th>
                            <th className="text-left px-4 py-2 font-medium">Revisions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-zinc-800/30">
                          {shots.slice(0, 30).map(s => (
                            <tr key={s.id} className="hover:bg-zinc-800/20 transition-colors">
                              <td className="px-4 py-2 text-xs font-mono text-zinc-300">{s.name}</td>
                              <td className="px-4 py-2">
                                <div className="flex gap-0.5">{Array.from({length: 5}).map((_, i) => (
                                  <div key={i} className={`w-2 h-2 rounded-sm ${i < (s.complexity || 3) ? 'bg-amber-500' : 'bg-zinc-800'}`} />
                                ))}</div>
                              </td>
                              <td className="px-4 py-2 text-[10px] font-mono text-zinc-500">{s.duration_seconds ? `${s.duration_seconds}s` : '-'}</td>
                              <td className="px-4 py-2"><span className={`status-${s.status} px-1.5 py-0.5 rounded-full text-[9px] font-medium`}>{s.status?.replace('_', ' ')}</span></td>
                              <td className="px-4 py-2 text-[10px] font-mono text-zinc-500">{s.revision_count || 0}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                </>)}
                </>
                )}
              </div>
          )}
        </>
      )}
      {deleteTarget && <DeleteProjectModal project={deleteTarget} onConfirm={(c) => doDelete(deleteTarget, c)} onClose={() => setDeleteTarget(null)} />}
      {showConvert && <ConvertToShowModal project={selectedProject} form={convertForm} setForm={setConvertForm} onConfirm={doConvert} onClose={() => setShowConvert(false)} />}
      {showAddEpisode && <AddEpisodeModal show={selectedProject} form={episodeForm} setForm={setEpisodeForm} onConfirm={doAddEpisode} onClose={() => setShowAddEpisode(false)} />}
    </div>
  );
}

// ────────────────────── Delete · Branching modals ──────────────────────
const DeleteProjectModal = ({ project, onConfirm, onClose }) => {
  const [confirm, setConfirm] = useState('');
  const ok = confirm === project.name;
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center" onClick={onClose}>
      <div className="bg-zinc-900 border border-rose-500/40 rounded-sm p-5 w-[28rem]" onClick={e => e.stopPropagation()} data-testid="delete-project-modal">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-8 h-8 rounded-full bg-rose-500/20 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-4 h-4 text-rose-400" />
          </div>
          <div>
            <h3 className="text-sm font-heading font-semibold text-white">Delete project permanently?</h3>
            <p className="text-[11px] text-zinc-500 mt-1">This will remove <span className="text-rose-300">{project.name}</span>{project.is_show && ' AND every episode under it'} along with all MDL assets, shots, tasks, allotments, reviews, briefs and access grants. This cannot be undone.</p>
          </div>
        </div>
        <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-3 mb-3">
          <p className="text-[10px] text-zinc-500 mb-1">Type <span className="text-amber-300 font-mono">{project.name}</span> to confirm:</p>
          <input value={confirm} onChange={e => setConfirm(e.target.value)} data-testid="delete-confirm-input"
            className="w-full px-2 py-1.5 bg-zinc-900 border border-zinc-700 rounded-sm text-[11px] text-white focus:outline-none focus:border-rose-500" />
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="text-[11px] px-3 py-1.5 text-zinc-400 hover:text-white">Cancel</button>
          <button onClick={() => onConfirm(confirm)} disabled={!ok} data-testid="delete-confirm-btn"
            className="text-[11px] px-3 py-1.5 bg-rose-500 disabled:opacity-30 hover:bg-rose-400 rounded-sm text-white font-medium">
            <Trash2 className="w-3 h-3 inline mr-1" /> Delete
          </button>
        </div>
      </div>
    </div>
  );
};
const ConvertToShowModal = ({ project, form, setForm, onConfirm, onClose }) => (
  <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center" onClick={onClose}>
    <div className="bg-zinc-900 border border-violet-500/40 rounded-sm p-5 w-[30rem]" onClick={e => e.stopPropagation()} data-testid="convert-show-modal">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-8 h-8 rounded-full bg-violet-500/20 flex items-center justify-center flex-shrink-0">
          <Tv className="w-4 h-4 text-violet-400" />
        </div>
        <div>
          <h3 className="text-sm font-heading font-semibold text-white">Convert {project.name} into a Show</h3>
          <p className="text-[11px] text-zinc-500 mt-1">All current MDL assets, shots, tasks and access grants will move into a new <span className="text-violet-300">Episode 01</span> child project. {project.name} becomes the parent Show — you can add more episodes (Story 02, Story 03…) later, each with its own independent folder structure.</p>
        </div>
      </div>
      <div className="space-y-2">
        <div>
          <label className="text-[9px] uppercase text-zinc-500">First episode name</label>
          <input value={form.episode_name} onChange={e => setForm({ ...form, episode_name: e.target.value })} data-testid="convert-ep-name"
            className="w-full mt-1 px-2 py-1.5 bg-zinc-950 border border-zinc-800 rounded-sm text-xs text-white focus:outline-none focus:border-violet-500" />
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <button onClick={onClose} className="text-[11px] px-3 py-1.5 text-zinc-400 hover:text-white">Cancel</button>
        <button onClick={onConfirm} disabled={!form.episode_name} data-testid="convert-confirm-btn"
          className="text-[11px] px-3 py-1.5 bg-violet-500 disabled:opacity-30 hover:bg-violet-400 rounded-sm text-white font-medium">
          <Tv className="w-3 h-3 inline mr-1" /> Convert to Show
        </button>
      </div>
    </div>
  </div>
);
const AddEpisodeModal = ({ show, form, setForm, onConfirm, onClose }) => (
  <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center" onClick={onClose}>
    <div className="bg-zinc-900 border border-violet-500/40 rounded-sm p-5 w-[30rem]" onClick={e => e.stopPropagation()} data-testid="add-episode-modal">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-8 h-8 rounded-full bg-violet-500/20 flex items-center justify-center flex-shrink-0">
          <GitBranch className="w-4 h-4 text-violet-400" />
        </div>
        <div>
          <h3 className="text-sm font-heading font-semibold text-white">Add Episode under {show.name}</h3>
          <p className="text-[11px] text-zinc-500 mt-1">Each episode gets its own folder structure, MDL, and shots — completely isolated from other episodes.</p>
        </div>
      </div>
      <div className="space-y-2">
        <div>
          <label className="text-[9px] uppercase text-zinc-500">Episode name *</label>
          <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
            placeholder="e.g. Cinderella · Snow White · Story 02" data-testid="add-ep-name"
            className="w-full mt-1 px-2 py-1.5 bg-zinc-950 border border-zinc-800 rounded-sm text-xs text-white focus:outline-none focus:border-violet-500" />
        </div>
        <div>
          <label className="text-[9px] uppercase text-zinc-500">Deadline</label>
          <input type="date" value={form.deadline} onChange={e => setForm({ ...form, deadline: e.target.value })} data-testid="add-ep-deadline"
            className="w-full mt-1 px-2 py-1.5 bg-zinc-950 border border-zinc-800 rounded-sm text-xs text-white focus:outline-none focus:border-violet-500" />
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <button onClick={onClose} className="text-[11px] px-3 py-1.5 text-zinc-400 hover:text-white">Cancel</button>
        <button onClick={onConfirm} disabled={!form.name} data-testid="add-ep-confirm-btn"
          className="text-[11px] px-3 py-1.5 bg-violet-500 disabled:opacity-30 hover:bg-violet-400 rounded-sm text-white font-medium">
          <Plus className="w-3 h-3 inline mr-1" /> Create Episode
        </button>
      </div>
    </div>
  </div>
);


// ────────────────────── Show Episode Picker ──────────────────────
// A SHOW is a container, not a workspace. It must NOT expose Master Library /
// Cloud Storage / NAS / Chapters tabs — that data lives inside its episodes.
// This component replaces the tab strip and lists every episode with a
// CTA to drill in (which is where the real workspace lives).
function ShowEpisodePicker({ show, api, episodes, onPick, onAddEpisode, isAdmin, onImportComplete }) {
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importError, setImportError] = useState('');
  const fileInputRef = React.useRef(null);

  const handleImport = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      setImportError('Asset breakdown must be an .xlsx file');
      return;
    }
    setImportError('');
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post(`/projects/${show.id}/master-library/import-asset-breakdown`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(r.data);
      if (onImportComplete) onImportComplete();
    } catch (e) {
      setImportError(e?.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="show-episode-picker">
      <div className="bg-violet-500/5 border border-violet-500/30 rounded-sm p-4 flex items-start gap-3">
        <Tv className="w-5 h-5 text-violet-300 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <h3 className="text-sm font-heading font-semibold text-violet-100">
            {show.name} is a multi-episode Show
          </h3>
          <p className="text-[11px] text-zinc-300 mt-1 leading-relaxed">
            A Show is a container — the Master Data Library, Cloud Storage, NAS bridge and
            Submit-for-Approval workspace all live inside individual <span className="text-cyan-300">Episodes</span>.
            Pick an episode below to manage its screenplay, assets and folders.
          </p>
        </div>
        {isAdmin && (
          <button onClick={onAddEpisode} data-testid="show-add-episode-btn"
            className="h-7 px-3 bg-violet-500 hover:bg-violet-400 text-white text-[10px] font-medium rounded-sm flex items-center gap-1.5 flex-shrink-0">
            <Plus className="w-3 h-3" /> Add Episode
          </button>
        )}
      </div>

      {/* Show-level Asset Breakdown import — auto-routes per-episode sheets to matching child projects */}
      {isAdmin && (
        <div className="bg-emerald-500/5 border border-emerald-500/30 rounded-sm p-4" data-testid="show-asset-import-card">
          <div className="flex items-start gap-3">
            <FileSpreadsheet className="w-5 h-5 text-emerald-300 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-heading font-semibold text-emerald-100">
                Import Asset Breakdown (XLSX) — auto-routes to all episodes
              </h3>
              <p className="text-[11px] text-zinc-300 mt-1 leading-relaxed">
                Drop the client's multi-tab Asset Breakdown sheet here.
                Each tab named after an episode (🧜 Mermaid · 🏡 Hansel · 🌸 Thumbelina) will land in its matching episode's Master Data Library —
                Characters, Environments and Props will be split correctly.
                <span className="text-zinc-500"> This is the asset list — NOT the screenplay/shotlist (which goes through the Submission Pipeline tab inside each episode).</span>
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx"
                onChange={(e) => handleImport(e.target.files?.[0])}
                className="hidden"
                data-testid="show-asset-import-input"
              />
              <div className="flex items-center gap-2 mt-3">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={importing}
                  data-testid="show-asset-import-btn"
                  className="h-8 px-3 bg-emerald-500 hover:bg-emerald-400 text-zinc-950 text-[11px] font-medium rounded-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
                  {importing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                  {importing ? 'Parsing & importing…' : 'Choose XLSX & Import'}
                </button>
                {importing && (
                  <span className="text-[10px] text-zinc-400">Parser detects sheets, extracts assets, spawns pipeline tasks…</span>
                )}
              </div>
              {importError && (
                <div className="mt-3 bg-rose-500/10 border border-rose-500/40 rounded text-[11px] text-rose-200 px-3 py-2 flex items-start gap-2" data-testid="show-asset-import-error">
                  <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                  <span>{importError}</span>
                </div>
              )}
              {importResult && (
                <div className="mt-3 bg-emerald-500/10 border border-emerald-500/40 rounded text-[11px] text-emerald-100 px-3 py-2" data-testid="show-asset-import-result">
                  <div className="flex items-center gap-2 font-medium">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    Imported {importResult.counts?.assets_created || 0} assets · {importResult.counts?.tasks_created || 0} tasks across {Object.keys(importResult.counts?.per_episode || {}).length} episode(s).
                    {importResult.counts?.skipped_existing ? ` ${importResult.counts.skipped_existing} skipped (already exist).` : ''}
                  </div>
                  <ul className="mt-1.5 space-y-0.5 ml-5 text-[10px] text-zinc-300">
                    {Object.entries(importResult.counts?.per_episode || {}).map(([epName, info]) => (
                      <li key={epName} className="font-mono">
                        • <span className="text-emerald-200">{epName}</span>: {info.created} created · {info.skipped} skipped · {info.tasks} tasks
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {episodes.map(ep => {
          const progress = ep.shot_count > 0
            ? Math.round((ep.completed_shots / ep.shot_count) * 100)
            : 0;
          return (
            <button key={ep.id} onClick={() => onPick(ep)}
              data-testid={`show-episode-card-${ep.id}`}
              className="bg-zinc-900 border border-zinc-800 hover:border-cyan-500/40 rounded-sm p-4 text-left group transition-all">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] font-mono text-zinc-500">
                  EP{String(ep.episode_number || 0).padStart(2, '0')}
                </span>
                <span className="text-xs font-medium text-zinc-100 group-hover:text-cyan-300 transition-colors flex-1 truncate">
                  {ep.name}
                </span>
                <ChevronRight className="w-3.5 h-3.5 text-zinc-700 group-hover:text-cyan-400" />
              </div>
              <p className="text-[10px] text-zinc-500">
                {ep.fps || 24} fps · {ep.resolution || '1920x1080'}
                {ep.total_duration_minutes ? ` · ${ep.total_duration_minutes} min` : ''}
              </p>
              <div className="flex items-center gap-2 mt-3">
                <div className="flex-1 h-1 bg-zinc-800 rounded-full overflow-hidden">
                  <div className="h-full bg-cyan-500" style={{ width: `${progress}%` }} />
                </div>
                <span className="text-[10px] font-mono text-zinc-500">{progress}%</span>
              </div>
              <div className="flex gap-3 mt-1.5 text-[10px] text-zinc-500">
                <span>{ep.shot_count || 0} shots</span>
                <span>{ep.completed_shots || 0} done</span>
              </div>
            </button>
          );
        })}
        {episodes.length === 0 && (
          <div className="col-span-full bg-zinc-900 border border-dashed border-zinc-800 rounded-sm p-8 text-center">
            <Info className="w-6 h-6 text-zinc-600 mx-auto mb-2" />
            <p className="text-xs text-zinc-400">No episodes yet</p>
            {isAdmin && (
              <button onClick={onAddEpisode}
                className="mt-3 h-7 px-3 bg-violet-500 hover:bg-violet-400 text-white text-[10px] rounded-sm inline-flex items-center gap-1">
                <Plus className="w-3 h-3" /> Create Episode 01
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ────────────────────── Client-link strip ──────────────────────
// Sits inside a project's detail header. Shows the currently linked client
// org (if any) with an Unlink button, OR a "Link to Client" CTA that opens
// a dialog of all client orgs to pick from. Cascades through Show → Episodes
// on the backend and auto-grants project_access to client users.
function ClientLinkStrip({ api, project, onChanged }) {
  const [orgs, setOrgs] = useState(null);
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const linkedOrgId = project.client_org_id;
  const linkedOrgName = project.client_name;

  const openPicker = async () => {
    setShow(true); setErr('');
    if (!orgs) {
      try {
        const r = await api.get('/admin/clients/organizations');
        setOrgs(r.data.organizations || []);
      } catch (e) { setErr('Could not load client list'); }
    }
  };

  const link = async (orgId) => {
    setBusy(true); setErr('');
    try {
      const r = await api.post(`/admin/clients/organizations/${orgId}/link-project`,
        { project_id: project.id });
      const org = (orgs || []).find(o => o.id === orgId);
      onChanged({ client_org_id: orgId, client_name: org?.client_name || org?.name || '' });
      setShow(false);
      // Surface counts in a toast-like alert
      console.info('Link result:', r.data);
    } catch (e) { setErr(e.response?.data?.detail || 'Link failed'); }
    finally { setBusy(false); }
  };

  const unlink = async () => {
    if (!linkedOrgId) return;
    if (!window.confirm(`Unlink ${linkedOrgName || 'client'} from ${project.name}? Client users will lose access immediately. The project itself stays.`)) return;
    setBusy(true); setErr('');
    try {
      await api.delete(`/admin/clients/organizations/${linkedOrgId}/link-project/${project.id}`);
      onChanged({ client_org_id: null, client_name: null });
    } catch (e) { setErr(e.response?.data?.detail || 'Unlink failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-3 flex items-center gap-2 text-xs" data-testid="client-link-strip">
      <Building2 className="w-3.5 h-3.5 text-zinc-500" />
      <span className="text-zinc-500">Client:</span>
      {linkedOrgId ? (
        <>
          <span className="text-cyan-300 font-medium" data-testid="linked-client-name">{linkedOrgName || linkedOrgId}</span>
          {project.is_show && <span className="text-[10px] text-zinc-500">(propagates to all episodes)</span>}
          <button onClick={unlink} disabled={busy} data-testid="unlink-client-btn"
            className="ml-2 text-[10px] px-2 py-0.5 rounded text-rose-300 hover:bg-rose-500/10 border border-rose-500/30 flex items-center gap-1">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Unlink className="w-3 h-3" />} Unlink
          </button>
        </>
      ) : (
        <>
          <span className="text-zinc-600 italic">— not linked —</span>
          <button onClick={openPicker} data-testid="link-client-btn"
            className="ml-2 text-[10px] px-2 py-0.5 rounded text-cyan-300 hover:bg-cyan-500/10 border border-cyan-500/30 flex items-center gap-1">
            <LinkIcon className="w-3 h-3" /> Link to Client
          </button>
        </>
      )}
      {err && <span className="text-rose-300 text-[10px] ml-2">{err}</span>}

      {/* Picker modal */}
      {show && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center" onClick={() => setShow(false)}>
          <div className="bg-zinc-900 border border-zinc-800 rounded-md p-5 w-[28rem] max-h-[80vh] flex flex-col"
               onClick={e => e.stopPropagation()} data-testid="link-client-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-heading font-semibold text-white flex items-center gap-2">
                <LinkIcon className="w-4 h-4 text-cyan-400" /> Link &ldquo;{project.name}&rdquo; to Client
              </h3>
              <button onClick={() => setShow(false)}><X className="w-4 h-4 text-zinc-500" /></button>
            </div>
            <p className="text-[11px] text-zinc-400 mb-3">
              Pick a client organisation. The project {project.is_show ? 'and all its episodes' : ''} will be
              tagged with this client and every <span className="text-cyan-300">client_producer</span> /
              <span className="text-cyan-300"> client_director</span> in that org will gain
              view-access to upload reviews & approve work.
            </p>
            {orgs === null ? (
              <div className="py-6 text-center text-xs text-zinc-500"><Loader2 className="w-4 h-4 animate-spin mx-auto mb-1" /> Loading…</div>
            ) : orgs.length === 0 ? (
              <div className="py-6 text-center text-xs text-zinc-500">
                <Building2 className="w-6 h-6 mx-auto mb-2 text-zinc-700" />
                No client organisations yet. Create one via the <span className="text-cyan-300">New Client</span> wizard first.
              </div>
            ) : (
              <div className="space-y-1.5 overflow-y-auto pr-1" data-testid="link-client-org-list">
                {orgs.map(o => (
                  <button key={o.id} onClick={() => link(o.id)} disabled={busy}
                    data-testid={`link-client-org-${o.id}`}
                    className="w-full text-left px-3 py-2 bg-zinc-950 hover:bg-zinc-800/60 border border-zinc-800 hover:border-cyan-500/40 rounded-sm transition-all">
                    <p className="text-xs text-zinc-100 font-medium">{o.client_name || o.name || '(unnamed)'}</p>
                    <p className="text-[10px] text-zinc-500">{o.legal_entity_name || o.country_of_incorporation || o.id?.slice(0, 8)}</p>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

